import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface SessionUser {
  userId: string
  username: string
  rol: string
  numeComplet: string
  punctLucruId: string
  punctCodul: string        // ex. PL0001
  punctDenumire: string     // ex. "Depozit Central"
  storeDbId: string
  puncteLucruAcces: string[] | null  // null = admin (acces total)
  token: string
}

interface AuthState {
  session: SessionUser | null
  /** Token de download (scope 'file') — ținut în memoria tabului, nu în localStorage. */
  fileToken: string | null
  setSession: (s: SessionUser | null) => void
  setFileToken: (t: string | null) => void
  logout: () => void
}

/**
 * Rolul `vizualizare` este read-only, iar backendul îi refuză ORICE mutație
 * (readOnlyGuard din `protectedProcedure`). Interfața afișa însă toate butoanele de
 * creare/editare/ștergere, deci utilizatorul le apăsa și primea o eroare garantată.
 * Folosește-l ca să ascunzi acțiunile, nu ca graniță de securitate — aceea e pe server.
 */
export function usePoateModifica(): boolean {
  const rol = useAuthStore(s => s.session?.rol)
  return rol !== 'vizualizare'
}

// La logout trebuie golit și cache-ul React Query, altfel datele utilizatorului anterior
// (sau ale punctului de lucru anterior) rămân vizibile pentru următoarea sesiune. queryClient
// trăiește în App.tsx (nu se poate importa aici fără ciclu) → îl înregistrăm printr-un callback.
let onLogoutCleanup: (() => void) | null = null
export function registerLogoutCleanup(fn: () => void) { onLogoutCleanup = fn }

// La comutarea punctului de lucru (și la logout) starea UI legată de tenant — navigarea,
// alertele — trebuie resetată. Aceleași motive de ciclu ca mai sus → callback înregistrat.
let onSessionScopeChange: (() => void) | null = null
export function registerSessionScopeReset(fn: () => void) { onSessionScopeChange = fn }

/** Cheia sub care `persist` salvează sesiunea în localStorage (citită și de sincronizarea între taburi). */
export const AUTH_STORAGE_KEY = 'auth-session'

// Când ALT tab schimbă sesiunea (logout / alt punct de lucru / alt cont), tabul acesta se
// reîncarcă. Dacă utilizatorul anulează reîncărcarea (dialogul „Părăsiți pagina?" al unui
// formular nesalvat), tabul rămâne cu sesiunea VECHE în memorie — și nu are voie s-o mai scrie
// înapoi în localStorage: un simplu setFileToken ar fi readus sesiunea veche în TOATE taburile
// (o deconectare pe un PC partajat s-ar fi anulat singură). Revizia din 5 nov 2026.
let sesiuneInvechita = false
export function marcheazaSesiuneInvechita() { sesiuneInvechita = true }
const stocareSesiune = createJSONStorage(() => ({
  getItem:    (k: string) => localStorage.getItem(k),
  setItem:    (k: string, v: string) => { if (!sesiuneInvechita) localStorage.setItem(k, v) },
  removeItem: (k: string) => { if (!sesiuneInvechita) localStorage.removeItem(k) },
}))

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      fileToken: null,
      setSession: (s) => {
        const plVechi = get().session?.punctLucruId
        if (!sesiuneInvechita) {
          if (s) localStorage.setItem('token', s.token)
          else localStorage.removeItem('token')
        }
        // Tokenul de fișiere rămâne valabil cât timp tokenul de sesiune e același; la schimbare
        // (parolă nouă, „deconectează celelalte sesiuni", comutare PL) se invalidează, iar App
        // cere imediat unul nou (efectul pe session.token) — altfel linkurile /files dădeau 401
        // până la refetch-ul de 90 de minute.
        const pastreazaFileToken = !!s && get().session?.token === s.token
        set({ session: s, fileToken: pastreazaFileToken ? get().fileToken : null })
        if (plVechi && plVechi !== s?.punctLucruId) onSessionScopeChange?.()
      },
      setFileToken: (t) => set({ fileToken: t }),
      logout: () => {
        if (!sesiuneInvechita) {
          localStorage.removeItem('token')
          localStorage.removeItem('fileToken')
        }
        set({ session: null, fileToken: null })
        onLogoutCleanup?.()
        onSessionScopeChange?.()
      },
    }),
    { name: AUTH_STORAGE_KEY, storage: stocareSesiune, partialize: (s) => ({ session: s.session }) },
  ),
)

/**
 * Tokenul cu care pleacă cererile acestui tab. Sursa de adevăr este sesiunea din memoria
 * tabului, NU `localStorage['token']`: localStorage e comun tuturor taburilor, deci după o
 * comutare de punct de lucru în tabul A, tabul B ar fi trimis în tăcere tokenul punctului A
 * în timp ce afișa încă punctul B → operații în alt tenant. Fallback pe localStorage doar
 * fără sesiune (pasul 2 al login-ului, când tokenul provizoriu nu e încă o sesiune).
 */
export function getAuthToken(): string | null {
  const session = useAuthStore.getState().session
  if (session) return session.token
  try { return localStorage.getItem('token') } catch { return null }
}

/**
 * Token pentru URL-urile /files?t= — EXCLUSIV cel restrâns (scope 'file'). Fără fallback pe
 * tokenul de sesiune: un URL ajunge în loguri/Referer, iar sesiunea dă acces complet la tRPC.
 */
export function getFileToken(): string | null {
  return useAuthStore.getState().fileToken ?? null
}

/** Tokenul sesiunii dintr-o valoare brută `auth-session` din localStorage (sau null). */
export function tokenDinSesiuneSalvata(raw: string | null): string | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { state?: { session?: { token?: unknown } | null } }
    const t = parsed?.state?.session?.token
    return typeof t === 'string' ? t : null
  } catch {
    return null
  }
}
